/**
 * Lazy loader for third-party embed scripts (the Jotform reviews widget).
 *
 * An embed opts in with `data-lazy-embed-src="<script url>"` on its container.
 * The script is injected right after the container (so the vendor finds its div
 * exactly as it would with a plain <script> tag) when the first of these happens:
 *   - the container comes within 600px of the viewport (IntersectionObserver);
 *   - the visitor first interacts with the page: scroll, pointerdown, keydown or
 *     touchstart.
 * Without IntersectionObserver only the interaction trigger is used.
 *
 * A container is injected once, marked with data-lazy-embed-loaded. A load failure
 * goes to window.reportClientError when the website error reporter is present.
 * Safe to include more than once on a page (the component is included once per
 * widget): the second copy is a no-op.
 */
(function () {
    'use strict';

    if (window.__lazyEmbedInit) return;
    window.__lazyEmbedInit = true;

    var INTERACTION_EVENTS = ['scroll', 'pointerdown', 'keydown', 'touchstart'];
    var ROOT_MARGIN = '600px 0px';

    function report(err, src) {
        try {
            if (typeof window.reportClientError === 'function') {
                window.reportClientError(err, { source: 'lazy-embed', context: { src: src } });
            }
        } catch (e) { /* reporting must never throw */ }
    }

    function inject(el, observer) {
        if (el.hasAttribute('data-lazy-embed-loaded')) return;
        var src = el.getAttribute('data-lazy-embed-src');
        if (!src) return;
        el.setAttribute('data-lazy-embed-loaded', '');
        if (observer) observer.unobserve(el);

        var script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onerror = function () {
            report(new Error('Lazy embed script failed to load: ' + src), src);
        };
        el.after(script);
    }

    function init() {
        var els = Array.prototype.slice.call(document.querySelectorAll('[data-lazy-embed-src]'));
        if (!els.length) return;

        var observer = null;
        if ('IntersectionObserver' in window) {
            observer = new IntersectionObserver(function (entries) {
                entries.forEach(function (entry) {
                    if (entry.isIntersecting) inject(entry.target, observer);
                });
            }, { rootMargin: ROOT_MARGIN });
            els.forEach(function (el) { observer.observe(el); });
        }

        var fired = false;
        function onInteraction() {
            if (fired) return;
            fired = true;
            INTERACTION_EVENTS.forEach(function (name) {
                window.removeEventListener(name, onInteraction);
            });
            if (observer) observer.disconnect();
            els.forEach(function (el) { inject(el, null); });
        }
        INTERACTION_EVENTS.forEach(function (name) {
            window.addEventListener(name, onInteraction, { once: true, passive: true });
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
